/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { RootStore } from 'src/core/stores/RootStore';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { HierarchicalEntity } from 'src/luis/projects/entities/types/HierarchicalEntity';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { resolveEntityStore } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { VersionedAppId } from 'src/luis/types/VersionedAppId';

/**
 * A helper function to check the type of the given
 * object and cast it to the given type.
 *
 * @param entity The entity to check.
 * @param entityType The type to compare.
 * @returns A boolean while casting entity as the
 * given generic type T.
 */
export const isEntityOfType = <T extends Entity>(entity: Entity, entityType: ModelType): entity is T => entity.typeId === entityType;

/**
 * A helper function to check the type of the given
 * object and cast it to the given type.
 *
 * @param entity The entity to check.
 * @param entityType The type to compare.
 * @returns A boolean while casting entity as the
 * given generic type T.
 */
export const isEntityOfStoreType = <T extends EntityStore>(entity: EntityStore, entityType: ModelType): entity is T =>
    entity.typeId === entityType;

/**
 * Returns true if the given entity is a machine learning entity.
 */
export const isMachineLearned = (entityType: ModelType): boolean => {
    switch (entityType) {
        case ModelType.Regex:
        case ModelType.List:
        case ModelType.Prebuilt:
        case ModelType.PatternAny:
            return false;
        default:
            return true;
    }
};

/**
 * Returns the entity of the given id in any depth.
 */
export const getEntityStoreOrEntityChildStoreById = (id: LuisId, entityStores: EntityStore[]): EntityStore => {
    let entity = entityStores.find(e => e.id === id);

    if (!entity) {
        entityStores
            .filter(e => e.typeId === ModelType.Simple || e.typeId === ModelType.Hierarchical)
            .forEach(e => {
                const { entity: foundEntity } = resolveEntityStore({ entityStores, parentId: e.id, entityId: id });
                if (foundEntity) {
                    entity = foundEntity;
                }
            });
    }

    return entity;
};

/**
 * Prints a user friendly representation of each entity type.
 *
 * @param entityType The type to print.
 * @returns A pretty string representation of the type.
 */
export const prettyPrintEntity = (entityType: ModelType) => {
    switch (entityType) {
        case ModelType.List:
            return 'List';
        case ModelType.Composite:
            return 'Composite';
        case ModelType.PatternAny:
            return 'Pattern.any';
        case ModelType.Prebuilt:
            return 'Prebuilt';
        case ModelType.Regex:
            return 'Regex';
        case ModelType.Simple:
            return 'Machine learned';
        case ModelType.Hierarchical:
            return 'Hierarchical';
        case ModelType.HierarchicalChild:
            return 'Hierarchical Child';
        case ModelType.Role:
            return 'Role';
        default:
            return '';
    }
};

/**
 * Gets the url based on the entity type.
 *
 * @param entityType The entity type of the entity to get
 * the url for.
 * @returns The url string.
 */
export const getEntityUrlName = (entityType: ModelType): string => {
    switch (entityType) {
        case ModelType.List:
            return 'closedLists';
        case ModelType.Composite:
            return 'compositeEntities';
        case ModelType.PatternAny:
            return 'patternAnyEntities';
        case ModelType.Regex:
            return 'regexEntities';
        case ModelType.Simple:
            return 'entities';
        case ModelType.Prebuilt:
            return 'prebuilts';
        default:
            throw new Error(`${prettyPrintEntity(entityType)} is not defined yet!`);
    }
};

/**
 * Given an array of entities, returns an array of
 * all the roles that these entities own.
 *
 * @param entities The entities to get roles from.
 * @returns An array of all the roles in the entities.
 */
export const getEntityRoles = (entities: Entity[]): EntityRole[] => flatten(entities.map(e => e.roles));

/**
 * Gets the composite entity allowed children
 * if the entity type role it returns parent:role name.
 *
 * @param entities The all entities to filter from.
 * @returns Composite children allowed entities.
 */
export const getCompositeEntityChildren = (entities: Entity[]): Partial<Entity>[] => {
    // These types are not allowed as children to composite entities.
    const disallowedTypes = [ModelType.Composite, ModelType.PatternAny];

    /**
     * Allowed composite entitites are:
     * 1. Roles of allowed entity types.
     * 2. Allowed entity types.
     * 3. Hierarchical child entities.
     */
    return flatten(
        entities
            .filter(e => disallowedTypes.every(t => t !== e.typeId))
            .map(e => e.roles.map(r => ({ id: r.id, name: `${e.name}:${r.name}` })))
    )
        .concat(entities.filter(e => disallowedTypes.every(t => t !== e.typeId)).map(e => ({ id: e.id, name: e.name })))
        .concat(
            flatten(
                entities
                    .filter(e => e.typeId === ModelType.Hierarchical)
                    .map(e => (<HierarchicalEntity>e).children.map(c => ({ id: c.id, name: `${e.name}::${c.name}` })))
            )
        );
};

/**
 * Checks if the given entity supports features or not.
 * There are two entity types that support features:
 *
 * - Simple Entities
 * - Composite Entities
 *
 * @param entity The entity to check.
 * @returns True if the entity supports features
 * and false otherwise.
 */
export const doesEntitySupportFeatures = (entity: Entity): entity is SimpleEntity | CompositeEntity =>
    entity.typeId === ModelType.Simple || entity.typeId === ModelType.Composite;

export const getEntityHandlerGroupUtils = (rootStore: RootStore) => {
    /**
     * Gets the app version store that corresponds to the given app id
     * and version id.
     *
     * @param appId The id of the application to get.
     * @param versionId The id of the version to get.
     */
    const getAppVersionStore = ({ appId, versionId }: VersionedAppId) =>
        rootStore.luisAppStoreHouse.findById(appId).appVersionStoreHouse.findById(versionId);

    /**
     * Gets the Pattern.Any entity store that corresponds to the given app id, version id and Pattern.Any entity id.
     *
     * @param appId The id of the application to get.
     * @param versionId The id of the version to get.
     * @param entityId The id of the Pattern.Any entity to get.
     */
    const getEntityStore = <T extends EntityStore>({ appId, versionId, entityId }: VersionedAppId & { entityId: LuisId }) =>
        <T>getAppVersionStore({ appId, versionId }).entityStoreHouse.findById(entityId);

    const addOrReplaceEntityStore = ({ appId, versionId }: VersionedAppId, entityStore: EntityStore) => {
        const appVersionStore = getAppVersionStore({ appId, versionId });
        const store = appVersionStore.entityStoreHouse.findById(entityStore.id);
        if (store) {
            appVersionStore.entityStoreHouse.replace(entityStore);
        } else {
            appVersionStore.entityStoreHouse.add(entityStore);
        }
    };

    return { getAppVersionStore, getEntityStore, addOrReplaceEntityStore };
};

/**
 * Filters out
 * 1. The entity itself
 * 2. Entity's existing features
 * 3. Entity's constraint
 * from the list of all available features for any simple entity
 */
export const filterAvailableFeatures = (entity: Entity, features: Feature[]): Feature[] => {
    if (entity && (entity.typeId === ModelType.Composite || entity.typeId === ModelType.Simple)) {
        const instanceOfId =
            entity && entity.typeId === ModelType.Simple && (<SimpleEntity>entity).instanceOf
                ? (<SimpleEntity>entity).instanceOf.id
                : undefined;

        features = features.filter(
            feature =>
                entity &&
                feature.id !== entity.id &&
                (<SimpleEntity>entity).features.every(d => d.id !== feature.id) &&
                feature.id !== instanceOfId
        );
    }

    return features;
};
